import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "wagers-view" }
const _hoisted_2 = { class: "view-header" }
const _hoisted_3 = {
  key: 1,
  class: "auth-error"
}
const _hoisted_4 = {
  key: 3,
  class: "error-message"
}
const _hoisted_5 = {
  key: 4,
  class: "pagination-controls"
}

import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useTransactionsStore } from '@/stores/transactions'
import { useAuthStore } from '@/stores/auth'
import { useLoaderStore } from '@/stores/loader'
import { useToast } from '@/composables/useToast'
import ToastComponent from '@/components/ToastComponent.vue'
import TransactionsTable from '@/components/wagers/TransactionsTable.vue'
import type { Transaction } from '@/types/transaction'
import BaseButton from '@/components/BaseButton.vue'
import { useBetslipStore } from '@/stores/betslip'
import { useGameStore } from '@/stores/game'
import { useModalStore } from '@/stores/modal'
import { useRoute } from 'vue-router'
import { iframeCommunication } from '@/utils/iframeCommunication'
import BaseModal from '@/components/BaseModal.vue'
import TransactionDetails from '@/components/wagers/TransactionDetails.vue'
import { useI18n } from 'vue-i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'WagersView',
  setup(__props) {

const transactionsStore = useTransactionsStore()
const authStore = useAuthStore()
const loaderStore = useLoaderStore()
const { toast, showToast } = useToast()
const { t } = useI18n()

const betslipStore = useBetslipStore()
const { addBet } = betslipStore
const gameStore = useGameStore()
const route = useRoute()
const isDetailsModalOpen = ref(false)
const savedScrollPosition = ref(0)

// Get the expand reference from query params (for auto-opening modal)
const expandReference = ref<string | null>(route.query.expand ? (route.query.expand as string) : null)

const isAuthenticated = computed(() => authStore.isAuthenticated)

const handleLoginRequest = () => {
  iframeCommunication.sendLoginRequest('wagers-view-required')
  return;
}

// Watch for transactions to be loaded and auto-expand if needed
watch(
  () => transactionsStore.transactions,
  (transactions) => {
    if (transactions && transactions.length > 0 && expandReference.value && !isDetailsModalOpen.value) {
      const transaction = transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        viewDetails(transaction)
      }
    }
  },
  { immediate: true }
)

const viewDetails = async (wager: Transaction) => {
  try {
    savedScrollPosition.value = window.pageYOffset || document.documentElement.scrollTop
    loaderStore.show(t('wagers.loadingDetails'))
    await transactionsStore.fetchTransactionDetail(wager.reference)
    isDetailsModalOpen.value = true
  } catch (err) {
    isDetailsModalOpen.value = false
  } finally {
    loaderStore.hide()
  }
}

const closeDetailsModal = () => {
  isDetailsModalOpen.value = false
  nextTick(() => {
    window.scrollTo({
      top: savedScrollPosition.value,
      behavior: 'auto'
    })
  })
}

const goToPage = (page: number) => {
  transactionsStore.setPage(page)
}

const handleRebet = async () => {
  try {
    const transaction = transactionsStore.currentTransaction
    if (!transaction || !transaction.games) {
      showToast({
        title: t('common.error'),
        message: t('betslip.noTransactionData'),
        type: 'error'
      })
      return
    }

    let rebetCount = 0
    
    for (const [gameKey, games] of Object.entries(transaction.games)) {
      for (const game of (games as any[])) {
        await gameStore.fetchGameDetails(gameKey)
        const nextDraws = gameStore.getNextDraws
        const marketGroups = gameStore.getMarketGroups
        
        for (const bet of game.bets) {
          // Find matching market and outcome based on bet information
          let matchedMarket = null
          let matchedOutcome = null
          let matchedMarketGroup = null
          
          // Try to find the market and outcome that matches this bet
          for (const marketGroup of marketGroups) {
            for (const market of marketGroup.markets) {
              // Match by market name and bet type
              if (market.name === bet.market_name && market.outcomes) {
                // Try to find matching outcome by odds and text
                for (const outcome of market.outcomes) {
                  if (Math.abs(parseFloat(outcome.odds) - parseFloat(bet.odds)) < 0.01) {
                    matchedMarket = market
                    matchedOutcome = outcome
                    matchedMarketGroup = marketGroup
                    break
                  }
                }
                if (matchedOutcome) break
              }
            }
            if (matchedOutcome) break
          }
          
          // If we couldn't find a match, skip this bet
          if (!matchedMarket || !matchedOutcome || !matchedMarketGroup) {
            console.warn('Could not find matching market/outcome for bet:', bet)
            continue
          }
          
          const betslipItem = {
            market_id: matchedMarket.id,
            market_group_id: matchedMarketGroup.id,
            game_name: gameStore.getGameDetail?.name,
            game_id: gameStore.getGameDetail?.id || 0,
            market_name: bet.market_name || '',
            market_description: bet.market_group_description || '',
            numbers: bet.bet_numbers || [],
            stake: Number(bet.bet_cost),
            betType: bet.betType,
            maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
            odds: Number(bet.odds),
            draws: nextDraws && nextDraws.length > 0 ? [nextDraws[0]] : [],
            allAvailableDraws: nextDraws || [],
            drawCount: 1,
            picks: (bet as any).picks || 0,
            numBets: 1,
            outcome_id: matchedOutcome.id.toString(),
            label: bet.market_name || '',
            specifier: matchedOutcome.specifier || null,
            select_numbers: matchedOutcome.select_numbers || false,
            currency_symbol: gameStore.getBetSettings?.currency_symbol || (transaction as any).currency_symbol || '€'
          }
          
          const added = addBet(betslipItem)
          if (added) {
            rebetCount++
          }
        }
      }
    }

    const modalStore = useModalStore()
    isDetailsModalOpen.value = false
    modalStore.openBetslip()
  } catch (error) {
    console.error('Rebet error:', error)
    showToast({
      title: t('common.error'),
      message: t('betslip.failedToAddBets'),
      type: 'error'
    })
  }
}

onMounted(async () => {
  try {
    // Check if authentication parameters are present in URL or session
    const route = useRoute()
    const hasToken = !!route.query.token
    const hasAccount = !!route.query.account
    const hasSessionAuth = authStore.hasValidSession()
    
    if (!hasToken && !hasAccount && !hasSessionAuth) {
      return // Silently return without showing message
    }
    
    const authSuccess = await authStore.authenticate()
    
    if (!authSuccess) {
      return
    }
    
    await transactionsStore.fetchTransactions()
    
    // Check if there's an expand query parameter to auto-expand a transaction
    if (expandReference.value && !isDetailsModalOpen.value) {
      // Find the transaction with this reference
      const transaction = transactionsStore.transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        // Auto-expand this transaction
        await viewDetails(transaction)
      }
    }
  } catch (err: any) {
    console.log(err)
    
    // Check if it's a 401 error (authentication failed)
    if (err?.response?.status === 401) {
      authStore.clearInvalidTokenKeepAccount()
      // Don't return here - let the component render the auth error message
    } else {
      showToast({
        title: t('common.error'),
        message: t('toast.failedToLoadWagers'),
        type: 'error'
      })
    }
  } finally {
    loaderStore.hide()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h1", null, _toDisplayString(_ctx.$t('nav.wagers')), 1)
    ]),
    (_unref(toast))
      ? (_openBlock(), _createBlock(ToastComponent, {
          key: 0,
          title: _unref(toast).title,
          message: _unref(toast).message,
          type: _unref(toast).type,
          duration: _unref(toast).duration
        }, null, 8, ["title", "message", "type", "duration"]))
      : _createCommentVNode("", true),
    (!isAuthenticated.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("p", null, [
            _createTextVNode(_toDisplayString(_ctx.$t('auth.authenticationRequired')) + " ", 1),
            _createElementVNode("a", {
              href: "#",
              onClick: _withModifiers(handleLoginRequest, ["prevent"]),
              class: "login-link"
            }, _toDisplayString(_ctx.$t('auth.pleaseLoginLink')), 1),
            _cache[2] || (_cache[2] = _createTextVNode("."))
          ])
        ]))
      : (_openBlock(), _createBlock(TransactionsTable, {
          key: 2,
          transactions: _unref(transactionsStore).transactions,
          loading: _unref(transactionsStore).loading,
          onViewDetails: viewDetails,
          onRebet: handleRebet
        }, null, 8, ["transactions", "loading"])),
    _createVNode(BaseModal, {
      "is-open": isDetailsModalOpen.value,
      title: _ctx.$t('wagers.details'),
      onClose: closeDetailsModal
    }, {
      default: _withCtx(() => [
        (_unref(transactionsStore).currentTransaction)
          ? (_openBlock(), _createBlock(TransactionDetails, {
              key: 0,
              transaction: _unref(transactionsStore).currentTransaction,
              onRebet: handleRebet
            }, null, 8, ["transaction"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["is-open", "title"]),
    (_unref(transactionsStore).error)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(transactionsStore).error), 1))
      : _createCommentVNode("", true),
    (isAuthenticated.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(BaseButton, {
            variant: "outline",
            class: "pagination-button",
            disabled: _unref(transactionsStore).loading || _unref(transactionsStore).pagination.current_page === 1,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (goToPage(_unref(transactionsStore).pagination.current_page - 1)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.previous')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('common.page')) + " " + _toDisplayString(_unref(transactionsStore).pagination.current_page) + " " + _toDisplayString(_ctx.$t('common.of')) + " " + _toDisplayString(_unref(transactionsStore).pagination.total_pages), 1),
          _createVNode(BaseButton, {
            variant: "outline",
            class: "pagination-button",
            disabled: _unref(transactionsStore).loading || _unref(transactionsStore).pagination.current_page === _unref(transactionsStore).pagination.total_pages,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (goToPage(_unref(transactionsStore).pagination.current_page + 1)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.next')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})